import { useRef, useState } from 'react';
import Button from '../Button/Button';
import { ClickableAction, ClickableTarget } from '../Clickable/consts';
import FormFieldSuggestion from '../FormFieldSuggestion/FormFieldSuggestion';
import {
  dataToOptions,
  filterOptions,
  findSelectedOptionValue,
} from './helpers';
import { useSmartChargingBrands } from './hooks/useSmartChargingBrands';
import { useSmartChargingModels } from './hooks/useSmartChargingModels';
import styles from './SmartChargingBetaAppSelector.module.scss';

import type { FormFieldElement } from '../FormField/interfaces';
import type { SuggestionOption } from '../FormFieldSuggestion/interfaces';

const initialState = {
  brand: {
    inputValue: '',
    selected: '',
  },
  model: {
    inputValue: '',
    selected: '',
  },
};

const SmartChargingBetaAppSelector = () => {
  const brandFieldRef = useRef<HTMLDivElement>(null);
  const [state, setState] = useState(initialState);
  const brands = useSmartChargingBrands();
  const models = useSmartChargingModels(state.brand.selected);

  const brandOptions = dataToOptions(brands.data);
  const modelOptions = dataToOptions(models.data);

  const selectorOptions = {
    brand: brandOptions,
    model: modelOptions,
  };

  const brandSuggestions = filterOptions(
    state.brand.inputValue,
    brandOptions,
    !state.brand.selected
  );

  const modelsSuggestions = filterOptions(
    state.model.inputValue,
    modelOptions,
    !!state.brand.selected && !state.model.selected
  );

  const onChange = ({ target }: React.ChangeEvent<FormFieldElement>) => {
    const { value, name } = target;

    setState((prevState) => {
      if (name === 'brand' || name === 'model') {
        return {
          ...(name === 'brand' ? initialState : prevState),
          [name]: {
            inputValue: value,
            selected:
              findSelectedOptionValue(value, selectorOptions[name]) ?? '',
          },
        };
      }

      return prevState;
    });
  };

  const onSelectOption =
    (name: keyof typeof initialState) => (option: SuggestionOption) => {
      setState((prevState) => ({
        ...prevState,
        [name]: {
          inputValue: option.label,
          selected: option.value,
        },
      }));
    };

  const isNoModelsAvailable =
    !brands.isLoading &&
    !models.isLoading &&
    state.brand.selected &&
    !state.model.inputValue &&
    modelOptions.length < 1;

  const shouldShowButtons = !!state.brand.selected && !!state.model.selected;

  return (
    <div className={styles.base}>
      <div className={styles.fields}>
        <FormFieldSuggestion
          ref={brandFieldRef}
          inputId="car-brand"
          label="Hersteller Ihres Fahrzeugs"
          options={brandSuggestions}
          nativeOptions={brandSuggestions}
          onChange={onChange}
          value={state.brand?.inputValue ?? ''}
          name="brand"
          onSelectOption={onSelectOption('brand')}
          disableNativeSelect
        />
        {modelOptions.length > 0 && (
          <FormFieldSuggestion
            inputId="car-model"
            label="Modell Ihres Fahrzeugs"
            options={modelsSuggestions}
            nativeOptions={modelsSuggestions}
            onChange={onChange}
            name="model"
            value={state.model?.inputValue ?? ''}
            onSelectOption={onSelectOption('model')}
            autoFocus
            disableNativeSelect
          />
        )}
      </div>
      {isNoModelsAvailable && <em>Keine Modelle verfügbar</em>}
      {shouldShowButtons && (
        <div className={styles.buttons}>
          <Button
            actionType={ClickableAction.Linkextern}
            linkextern="https://testflight.apple.com/join/evFCWeHD"
            target={ClickableTarget.Blank}
          >
            Für iOS (Apple) herunterladen
          </Button>
          <Button
            actionType={ClickableAction.Linkextern}
            linkextern="https://play.google.com/store/apps/details?id=com.enercity.smartcharging"
            target={ClickableTarget.Blank}
          >
            Für Android herunterladen
          </Button>
        </div>
      )}
    </div>
  );
};

export default SmartChargingBetaAppSelector;
